import supabase from '../supabase/supabase';

const TABLE = 'amp_events_20261114';
const EVENT_CACHE_KEY = 'amp-events';

function makeShareToken() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID().replaceAll('-', '');
  }

  const bytes = new Uint8Array(24);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function readLegacyAnswers(eventId) {
  const read = (key, fallback) => {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback;
    } catch {
      return fallback;
    }
  };

  return {
    answers: read(`amp-planner-answers-${eventId}`, {}),
    timeline: read(`amp-timeline-${eventId}`, []),
    songs: read(`amp-songs-${eventId}`, []),
    playlists: read(`amp-playlists-${eventId}`, [])
  };
}

function toEvent(row) {
  return {
    ...row.event_data,
    clientAnswers: row.client_answers || {},
    shareToken: row.client_token
  };
}

function toRow(event, ownerId, existingRow) {
  const eventData = { ...event };
  delete eventData.clientAnswers;
  delete eventData.shareToken;
  delete eventData.internalNotes;
  delete eventData.privateNotes;

  return {
    owner_id: ownerId,
    event_id: String(event.id),
    event_data: { ...(existingRow?.event_data || {}), ...eventData },
    client_answers: event.clientAnswers ?? existingRow?.client_answers ?? {},
    client_token: event.shareToken || existingRow?.client_token || makeShareToken(),
    updated_at: new Date().toISOString()
  };
}

export async function loadOwnedEvents(ownerId) {
  const { data: rows, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('owner_id', ownerId)
    .order('updated_at', { ascending: false });

  if (error) throw error;

  // Supabase is the only source of events; the old browser cache is cleared.
  localStorage.removeItem(EVENT_CACHE_KEY);
  return (rows || []).map(toEvent);
}

export async function saveEventRecord(ownerId, event) {
  const eventId = String(event.id);
  const { data: existingRow, error: readError } = await supabase
    .from(TABLE)
    .select('event_data,client_answers,client_token')
    .eq('owner_id', ownerId)
    .eq('event_id', eventId)
    .maybeSingle();

  if (readError) throw readError;

  const row = toRow(event, ownerId, existingRow);
  const { data, error } = await supabase
    .from(TABLE)
    .upsert(row, { onConflict: 'owner_id,event_id' })
    .select('*')
    .single();

  if (error) throw error;
  return toEvent(data);
}

export async function deleteEventRecords(ownerId, eventIds) {
  const ids = eventIds.map(String);
  if (ids.length === 0) return;

  const { data, error } = await supabase
    .from(TABLE)
    .delete()
    .eq('owner_id', ownerId)
    .in('event_id', ids)
    .select('event_id');

  if (error) throw error;
  if ((data || []).length < new Set(ids).size) {
    throw new Error(
      'Supabase did not delete the event. Check that a delete policy exists on the events table.'
    );
  }
}

export async function fetchClientEvent(token) {
  const { data, error } = await supabase.rpc(
    'amp_get_client_event_20261114',
    { p_token: token }
  );

  if (error) throw error;
  if (!data?.event) return null;

  return {
    event: data.event,
    answers: data.answers || {}
  };
}

export async function saveClientPlannerState(token, state) {
  const { data, error } = await supabase.rpc(
    'amp_save_client_answers_20261114',
    { p_token: token, p_answers: state }
  );

  if (error) throw error;
  if (data !== true) throw new Error('This client link is no longer valid.');
}

export function createClientShareToken() {
  return makeShareToken();
}